/**
 * Wann nach einer Liegenschaft gefragt wird.
 *
 * Die Frage gehört an den *Übergang* nach „analysiert", nicht an den Zustand. Der Unterschied ist
 * der zwischen einer Frage und einem Schwall: Beim Öffnen des Dashboards sind sämtliche Läufe eines
 * Projekts fertig, und wer den Zustand abfragt, bekommt für jedes je gelesene Dokument einen
 * Dialog. Gefragt wird deshalb nur zu dem, was fertig *geworden* ist, während jemand zusieht.
 *
 * Deshalb der erste Blick als Aussaat: Was beim ersten Sehen schon fertig ist, gilt als bekannt.
 * Das kostet die Frage zu einem Lauf, der genau zwischen zwei Blicken durchlief — dafür gibt es
 * keinen Fall, in dem sich dreißig Dialoge übereinanderlegen.
 */

/** Die Zustände, in denen ein Lauf durch ist. „Unentschieden" ist ein Ergebnis, kein Fehlschlag. */
const FINISHED = ['completed', 'undecided'];

export const isAnalysed = (status: unknown): boolean =>
    FINISHED.indexOf(String(status ?? '')) >= 0;

export interface AnalysisStatusRow {
    _id?: string;
    status?: string;
}

/**
 * Welche Läufe seit dem letzten Blick fertig geworden sind.
 *
 * Nur die, die vorher schon gesehen wurden und da noch nicht fertig waren. Ein Satz, der zum ersten
 * Mal auftaucht und gleich fertig ist, zählt nicht — er kann aus einem anderen Fenster stammen oder
 * von gestern, und eine Frage dazu käme aus dem Nichts.
 */
export const analysedSince = (
    before: Map<string, string>,
    rows: AnalysisStatusRow[],
): string[] =>
    (rows ?? [])
        .filter((row) => {
            const id = row?._id ? String(row._id) : '';
            if (!id || !isAnalysed(row?.status)) return false;

            return before.has(id) && !isAnalysed(before.get(id));
        })
        .map((row) => String(row._id));

/** Der Stand für den nächsten Blick. */
export const statusesOf = (rows: AnalysisStatusRow[]): Map<string, string> =>
    new Map(
        (rows ?? [])
            .filter((row) => row?._id)
            .map((row) => [String(row._id), String(row.status ?? '')]),
    );

/**
 * Die Frage, die zu einer eben getroffenen Zuordnung gehört.
 *
 * Der zweite Weg, auf dem eine Bindung entsteht: Wo der Abgleich sich nicht entscheiden konnte,
 * wählt später ein Mensch. Auch dann kann ein Gebäude dazukommen, dessen Anschrift das Projekt nicht
 * kennt — und die Frage ist dieselbe.
 *
 * Eingegrenzt auf **das gewählte** Gebäude, nicht auf alle offenen Vorschläge des Laufs: Gefragt
 * wird zu dem, was gerade entschieden wurde. Sonst käme mit jeder beantworteten Zuordnung wieder
 * hoch, was beim Abschluss der Auswertung schon einmal abgelehnt wurde.
 */
export const proposalForBinding = <T extends { buildingId: string }>(
    proposals: T[],
    candidate: { objectId?: unknown; className?: unknown } | null | undefined,
): T | null => {
    if (!candidate || String(candidate.className ?? '') !== 'Building') return null;

    const objectId = String(candidate.objectId ?? '');
    if (!objectId) return null;

    return (proposals ?? []).find((proposal) => proposal.buildingId === objectId) ?? null;
};
